// src/prototype/layouts/EAFocusRouteWrapper.jsx
// ============================================================
// EA FOCUS ROUTE WRAPPER
// 
// Conditionally renders child content in:
// - AppShell (no sidebar) when EA Focus Mode is active
// - MainAppLayout (with sidebar) when not in EA Focus Mode
//
// Usage in App.jsx:
// <Route path="/client-profile" element={
//   <EAFocusRouteWrapper>
//     <ClientProfilePage />
//   </EAFocusRouteWrapper>
// } />
// ============================================================

import React from 'react';
import { useLocation } from 'react-router-dom';
import AppShell from '@/layouts/AppShell';
import MainAppLayout from '@/layouts/MainAppLayout';

/**
 * Detects EA Focus Mode from location state or query params
 */
function useIsEAFocusMode() {
  const location = useLocation();
  const locationState = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  
  return locationState.fromEA === true || searchParams.get('fromEA') === 'true';
}

/**
 * Route wrapper that conditionally renders children in appropriate layout
 * 
 * @param {React.ReactNode} children - Page component to render
 * @param {boolean} forceEA - Force EA mode (useful for testing)
 */
export function EAFocusRouteWrapper({ children, forceEA = false }) {
  const isEA = useIsEAFocusMode() || forceEA;

  if (isEA) {
    // EA Focus Mode: Use AppShell without sidebar
    return (
      <AppShell hideSecondaryNav={true}>
        {children}
      </AppShell>
    );
  }

  // Normal mode: Use MainAppLayout with sidebar
  return (
    <MainAppLayout>
      {children}
    </MainAppLayout>
  );
}

/**
 * Alternative: Wrapper that only handles EA mode, assumes parent provides normal layout
 * Use this if the route is already inside a layout block in App.jsx
 */
export function EAFocusContentWrapper({ children }) {
  // Just renders children - banner is handled by the page component
  return <>{children}</>;
}

export default EAFocusRouteWrapper;